/*
  The address bar, which this client did not use at all.

  Every piece of navigation — which project is open, which phase, which person is selected, which
  analytics pane — was React state and nothing else. So nothing was linkable, the back button did
  nothing, and a refresh returned you to the project list. On a product whose entire reason for
  existing over the frozen single-file app is that several people work the same project at once,
  "look at the stranded pane for Katherine" could not be sent to anybody.

  NO ROUTER LIBRARY. There are four fields and one screen; `history` and `popstate` are the whole
  API needed, and a router would bring a route tree, a matcher and a link component to express
  `/p/:id/:phase`. This is forty lines against ~15 packages, and it keeps the store untouched — the
  URL is a projection OF the state, not a second source of it.

  ONE DIRECTION AT A TIME, which is the part that goes wrong. State changes write the URL; the URL is
  read only on first load and on `popstate`. Reading it continuously would fight every setter — the
  classic loop where a state change writes the URL, the URL write triggers a read, and the read
  re-sets the state it came from.

  REPLACE, NOT PUSH, for everything except opening and closing a project. Clicking through five
  phases and a dozen people should not bury the previous project under eighteen back-button presses;
  what a reader means by "back" here is "out of this project", and only that is a navigation.
*/
import type { PhaseId } from './phases.js';

export interface Place {
  projectId: string | null;
  phase: PhaseId | null;
  /** The selected person or group, as the chart's node id. */
  node: string | null;
  /** Which analytics pane, when one has been chosen. */
  tab: string | null;
  /**
   * Which full-width surface is open over the canvas, if any.
   *
   * This is the field that makes a workbench a PLACE. Analytics, the TSA register and the entity
   * flow used to be members of a modal stack — reachable only by clicking, unlinkable, and gone on
   * refresh. "Look at the stranded pane" is the single most-sent sentence on an engagement like
   * this, and until this existed it could not be sent.
   */
  view: ViewName | null;
}

/** The surfaces that have an address. Anything else in that slot is a stale link, not a route. */
export type ViewName = 'analytics' | 'tsa' | 'entities' | 'transfers' | 'target';

const VIEWS: readonly ViewName[] = ['analytics', 'tsa', 'entities', 'transfers', 'target'];

const PHASES: readonly PhaseId[] = ['data', 'target', 'perimeter', 'transfers', 'tsa', 'remainco'];

/** A project id as the server mints it. Anything else in that slot is somebody's typo, not a route. */
const PROJECT_ID = /^p_[A-Za-z0-9_-]+$/;

export function readPlace(url: { pathname: string; search: string }): Place {
  const parts = url.pathname.split('/').filter(Boolean);
  const search = new URLSearchParams(url.search);

  const projectId = parts[0] === 'p' && parts[1] && PROJECT_ID.test(parts[1]) ? parts[1] : null;
  /*
    A phase is only read when it is one this app has. An unknown segment resolves to null rather than
    being passed through: `setPhase` would accept the string, the phase spine would render nothing
    selected, and the panel underneath would be blank with no explanation.
  */
  const raw = parts[2];
  const phase = raw && (PHASES as readonly string[]).includes(raw) ? (raw as PhaseId) : null;

  /*
    Same rule as the phase: an unrecognised name resolves to null rather than being passed through.
    A link to a surface this build does not have should land on the project, not on a blank canvas
    with the chart missing and nothing to say why.
  */
  const rawView = search.get('view');
  const view = rawView && (VIEWS as readonly string[]).includes(rawView) ? (rawView as ViewName) : null;

  return {
    projectId,
    phase: projectId ? phase : null,
    node: projectId ? search.get('node') : null,
    tab: projectId ? search.get('tab') : null,
    view: projectId ? view : null,
  };
}

export function placeToUrl(place: Place): string {
  if (!place.projectId) return '/';
  let path = `/p/${place.projectId}`;
  if (place.phase) path += `/${place.phase}`;

  const search = new URLSearchParams();
  if (place.view) search.set('view', place.view);
  if (place.node) search.set('node', place.node);
  if (place.tab) search.set('tab', place.tab);
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Write the place to the address bar, if it is not already there.
 *
 * The no-op check is what keeps this out of React's way: it is called from an effect on every
 * render, and pushing an identical entry would grow the history stack for every keystroke that
 * happens to re-render the shell.
 */
export function syncUrl(place: Place, mode: 'push' | 'replace'): void {
  const next = placeToUrl(place);
  const current = `${window.location.pathname}${window.location.search}`;
  if (next === current) return;
  if (mode === 'push') window.history.pushState(null, '', next);
  else window.history.replaceState(null, '', next);
}
